import { useEffect } from 'react';
import Player from 'react-player/file';
import cm from 'classnames';

import { useAppSelector } from 'store/store-hooks';
import { useResizeObserver } from 'hooks/use-resize-observer';
import { usePanZoomFormat } from './_mediator/use-pan-zoom-format';
import { usePanZoomDrag } from './_mediator/use-pan-zoom-drag';
import { getViewerSize } from './_mediator/pan-zoom.helper';
import { usePanZoomCb } from './_mediator/use-pan-zoom-cb';
import { usePanZoomContext } from './_pan-zoom.provider';

import styles from './_pan-zoom.module.scss';

const CSS__FRAME_WIDTH = '--frame-width';
const CSS__FRAME_HEIGHT = '--frame-height';
const CSS__FRAME_BOTTOM = '--frame-bottom';
const CSS__FRAME_LEFT = '--frame-left';
const CSS__VIEWER_WIDTH = '--viewer-width';
const CSS__VIEWER_HEIGHT = '--viewer-height';

interface Props {
  element: React.MutableRefObject<Player | HTMLImageElement | null>;
  isActive: boolean;
  init: boolean;
}

export const PanZoom = ({ element, isActive, init }: Props) => {
  const { rootEl, viewerEl, frameEl, zoomEl, panZoomRotate, ARViewerSize } = usePanZoomContext();
  const { onDragStart, onDragEnd } = usePanZoomDrag();
  const { fromClipConfig } = usePanZoomFormat();
  const { onZoomDelta } = usePanZoomCb();
  const rotateListener = useAppSelector((store) => store.editor.panZoomRotateListener);

  const panZoomInit = () => {
    if (!element.current) return;
    const viewer = getViewerSize(element.current, rotateListener.rotate);
    if (!viewer) return;
    rootEl.current!.style.setProperty(CSS__VIEWER_WIDTH, viewer.width + 'px');
    rootEl.current!.style.setProperty(CSS__VIEWER_HEIGHT, viewer.height + 'px');
    fromClipConfig();
  };

  useResizeObserver(rootEl, () => init && panZoomInit());

  useEffect(() => {
    const cb = (e: WheelEvent) => {
      e.preventDefault();
    };
    viewerEl.current?.addEventListener('wheel', cb);
    return () => viewerEl.current?.removeEventListener('wheel', cb);
  }, []);

  useEffect(() => {
    if (!init) return;
    panZoomInit();
  }, [init]);

  useEffect(() => {
    if (!frameEl.current) return;
    const { left, bottom, zoom } = panZoomRotate;
    const css = frameEl.current!.style;
    css.setProperty(CSS__FRAME_WIDTH, ARViewerSize.current.width * zoom + 'px');
    css.setProperty(CSS__FRAME_HEIGHT, ARViewerSize.current.height * zoom + 'px');
    css.setProperty(CSS__FRAME_BOTTOM, bottom + 'px');
    css.setProperty(CSS__FRAME_LEFT, left + 'px');
  }, [panZoomRotate]);

  useEffect(() => {
    panZoomInit();
  }, [rotateListener]);

  return (
    <div ref={rootEl} className={cm(styles.PanZoom, { [styles['PanZoom--active']]: isActive })}>
      <div
        ref={viewerEl}
        className={styles.PanZoom__viewer}
        onPointerDown={onDragStart}
        onPointerUp={onDragEnd}
        onWheel={onZoomDelta}
      >
        <div ref={frameEl} className={styles.PanZoom__frame} />
        <div ref={zoomEl} className={styles.PanZoom__zoomRect} />
      </div>
    </div>
  );
};
